import { isDevEnv } from "@buildwithflux/shared";
import { Euler, Group, Matrix4, Quaternion, Vector3 } from "three";
import { LayerOrientation } from "../../rules";
const parentSceneDummy = new Group();
const layerSceneDummy = new Group();
const nodeSceneDummy = new Group();
parentSceneDummy.add(layerSceneDummy);
layerSceneDummy.add(nodeSceneDummy);
// TODO: There must be a better way to compute this once in a second pass instead of doing it for every node separately
// Furthermore, we should not invert the layout transform but just keep the middle results
// TODO: not used - remove ?
export function getTransformBakedRules(bakedRules, parentNode, parentRootLayoutOrFootprint) {
    /**
     * TODO: Later, when we have generic type for AnyPcbNode, we should be able
     * to remove this force cast.
     *
     * export function getTransformBakedRules<T extends LayoutNodeSupportsTransformBakedRules>(node: AnyPcbNode<T>, ...) {
     *   // Then, it should automatically figure out the `bakedRules` for you!
     *   const position = bakedRules.position;
     * }
     */
    const position = bakedRules.position ?? new Vector3(0, 0, 0);
    const rotation = bakedRules.rotation ?? new Vector3(0, 0, 0);
    const parentNodeTransform = parentNode?.bakedRules;
    // NOTE: at least we can check at runtime here that the parent node is baked
    if (isDevEnv() && parentNode && !parentNodeTransform) {
        throw new Error(`Unbaked parent node: ${parentNode.name} ${parentNode.type}`);
    }
    // ⚠️ Minor gotcha - ties in with NOTE above. Accessing the bakedRules for parentNode should be done via "parentNodeTransform"
    // rather than by "parentNode?.bakedRules" - which will cause tests to fail. We've inadvertently included test projects in our
    // "PcbLayoutVirtualNodeGenerator" test suite that strictly speaking, are invalid.
    // Some test projects have a defined "parentNode" with undefined "bakedRules", meaning logic in the form of:
    // if(parentNode) { parentNode.bakedRules.layer; } // ❌ throws an error
    // where as:
    // if(parentNodeTransform) { parentNodeTransform.layer; } // ✅ works as expected
    // this is a subtly to be aware of
    const parentLayer = parentNodeTransform?.layer ?? LayerOrientation.top;
    const nodeLayer = bakedRules.layer;
    const isBottom = (layer) => layer === LayerOrientation.bottom; // If layer is unset, consider it top.
    const flipLayer = isBottom(parentLayer) !== isBottom(nodeLayer);
    parentSceneDummy.position.set(parentNodeTransform?.positionRelativeToDocument?.x ?? 0, parentNodeTransform?.positionRelativeToDocument?.y ?? 0, parentNodeTransform?.positionRelativeToDocument?.z ?? 0);
    parentSceneDummy.rotation.set(parentNodeTransform?.rotationRelativeToDocument?.x ?? 0, parentNodeTransform?.rotationRelativeToDocument?.y ?? 0, parentNodeTransform?.rotationRelativeToDocument?.z ?? 0);
    layerSceneDummy.position.set(position.x, position.y, position.z);
    layerSceneDummy.rotation.set(0, flipLayer ? Math.PI : 0, 0);
    nodeSceneDummy.rotation.set(rotation.x, rotation.y, rotation.z);
    nodeSceneDummy.updateWorldMatrix(false, true);
    const worldPosition = nodeSceneDummy.getWorldPosition(new Vector3());
    const quaternionTarget = nodeSceneDummy.getWorldQuaternion(new Quaternion());
    const worldRotation = new Euler().setFromQuaternion(quaternionTarget);
    const transformRelativeToDocument = nodeSceneDummy.matrixWorld.toArray();
    const positionRelativeToDocument = {
        x: worldPosition.x,
        y: worldPosition.y,
        z: worldPosition.z,
    };
    const rotationRelativeToDocument = {
        x: worldRotation.x,
        y: worldRotation.y,
        z: worldRotation.z,
    };
    // TODO: Later, when we have generic type for AnyPcbNode, we should be able
    // to remove this force cast.
    const parentLayoutBakedTransform = parentRootLayoutOrFootprint?.bakedRules || null;
    // NOTE: at least we can check at runtime here that the parent layout is baked
    if (isDevEnv() && parentRootLayoutOrFootprint && !parentLayoutBakedTransform) {
        throw new Error(`Unbaked parent layout: ${parentRootLayoutOrFootprint.name} ${parentRootLayoutOrFootprint.type}`);
    }
    const parentRotationQuaternion = new Quaternion()
        .setFromEuler(new Euler(parentLayoutBakedTransform?.rotation.x, parentLayoutBakedTransform?.rotation.y, parentLayoutBakedTransform?.rotation.z))
        .invert();
    const relativeRotQuat = parentRotationQuaternion.clone().multiply(quaternionTarget);
    const relativeRot = new Euler().setFromQuaternion(relativeRotQuat);
    const rotationRelativeToRootLayout = parentRootLayoutOrFootprint
        ? { x: relativeRot.x, y: relativeRot.y, z: relativeRot.z }
        : null;
    const relativePos = worldPosition;
    worldPosition.x -= parentLayoutBakedTransform?.position.x || 0;
    worldPosition.y -= parentLayoutBakedTransform?.position.y || 0;
    worldPosition.z -= parentLayoutBakedTransform?.position.z || 0;
    worldPosition.applyQuaternion(parentRotationQuaternion);
    const positionRelativeToRootLayout = parentRootLayoutOrFootprint
        ? { x: relativePos.x, y: relativePos.y, z: relativePos.z }
        : null;
    const transformRelativeToShapesLayer3D = new Matrix4().fromArray(transformRelativeToDocument);
    const parentLayoutTransformInv = parentLayoutBakedTransform?.transformRelativeToDocument
        ? new Matrix4().fromArray(parentLayoutBakedTransform.transformRelativeToDocument).invert()
        : null;
    if (parentLayoutTransformInv) {
        parentLayoutTransformInv.multiply(transformRelativeToShapesLayer3D);
    }
    const transformRelativeToShapesLayer = parentLayoutTransformInv
        ? [
            // We drop the 3rd row/col
            // The comment should explain why, to be useful 👆
            parentLayoutTransformInv.elements[0],
            parentLayoutTransformInv.elements[1],
            parentLayoutTransformInv.elements[3],
            parentLayoutTransformInv.elements[4],
            parentLayoutTransformInv.elements[5],
            parentLayoutTransformInv.elements[7],
            parentLayoutTransformInv.elements[12],
            parentLayoutTransformInv.elements[13],
            parentLayoutTransformInv.elements[15],
        ]
        : null;
    return {
        positionRelativeToDocument,
        rotationRelativeToDocument,
        transformRelativeToDocument,
        positionRelativeToRootLayout,
        rotationRelativeToRootLayout,
        transformRelativeToShapesLayer,
    };
}
//# sourceMappingURL=getTransformBakedRules.js.map